/*
 * This file is part of glowingblue/orell-rezensionen.
 *
 * Copyright (c) Glowing Blue AG.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import Component, { type ComponentAttrs } from 'flarum/common/Component';
import Alert from 'flarum/common/components/Alert';
import Stream from 'flarum/common/utils/Stream';
import type Mithril from 'mithril';
import type Article from 'flarum/orell-articles/models/Article';
import type LiteraryWork from 'flarum/orell-articles/models/LiteraryWork';
import { MAX_RATING } from '../config';
import ArticleRating from './ArticleRating';
import RatingSummary, { type AggregatedRating } from './RatingSummary';

export interface RatingsCardAttrs extends ComponentAttrs {
	/** The article the current user rates from this card. */
	article: Article;
	/** The work whose articles are aggregated into the histogram. */
	work?: LiteraryWork;
}

/**
 * `articles()` is typed `| false` for an absent relation, which every endpoint feeding this card
 * includes. The narrower signature comes first so that it wins overload resolution.
 */
type LoadedWork = { articles(): Article[] } & LiteraryWork;

/** The per-level rating counters this extension installs on `Article`. */
type RatingLevelKey = `rated${1 | 2 | 3 | 4 | 5}`;

export default class RatingsCard extends Component<RatingsCardAttrs> {
	/** The current user's own rating of `attrs.article`, or 0 when they have not rated it. */
	rating: Stream<number>;

	oninit(vnode: Mithril.Vnode<RatingsCardAttrs, this>) {
		super.oninit(vnode);

		const { article } = this.attrs;

		const ownReview = article.ownReview();

		this.rating = Stream(ownReview ? ownReview.rating() : 0);
	}

	onupdate(vnode: Mithril.VnodeDOM<RatingsCardAttrs, this>) {
		super.onupdate(vnode);
		const { article } = this.attrs;
		const ownReview = article.ownReview();

		this.rating(ownReview ? ownReview.rating() : 0);
	}

	view() {
		const { article, work } = this.attrs as Omit<RatingsCardAttrs, 'work'> & { work: LoadedWork };

		// Ratings and reviews across the whole work, not just this article.
		const totalReviewsCount = work.articles().reduce((total, article) => {
			return total + article.allReviewsCount();
		}, 0);

		const totalReviewCount = work.articles().reduce((total, article) => {
			return total + article.reviewCount();
		}, 0);

		const aggregatedRatings = Array.from(Array(MAX_RATING)).map<AggregatedRating>((_, i) => {
			const ratingLevel = i + 1;
			const ratingCount = work.articles().reduce((total, article) => {
				return total + article[`rated${ratingLevel}` as RatingLevelKey]();
			}, 0);
			return {
				rating: ratingLevel,
				count: ratingCount,
				percent: ratingCount / totalReviewsCount,
			};
		});

		const totalWeightedRating = aggregatedRatings.reduce((total, { rating, count }) => {
			return total + rating * count;
		}, 0);
		const aggregatedRating = totalReviewsCount > 0 ? totalWeightedRating / totalReviewsCount : 0;

		let canMakeSimpleRating = false;
		let canDeleteRating = false;
		if (app.session.user?.canCreateReviews() ?? false) {
			canMakeSimpleRating = true;
			canDeleteRating = !!(article?.ownReview() && article?.ownReview()?.isSimple());
		}

		if (!totalReviewsCount && !canMakeSimpleRating) {
			return null;
		}

		return (
			<div className={`RatingsCard ${!totalReviewsCount ? 'isEmpty' : 'hasRatings'}`}>
				{!!totalReviewsCount && (
					<div className="RatingsCard-row">
						<div className="RatingsCard-overall">
							<div className="RatingsCard-score">
								<div className="RatingsCard-score_value">
									{aggregatedRating % 1 === 0 ? aggregatedRating.toFixed(0) : aggregatedRating.toFixed(1)}
								</div>
								<div className="RatingsCard-score_out_of">
									{app.translator.trans('glowingblue-orell-rezensionen.forum.article_page.ratings_card.out_of', { maxRating: MAX_RATING })}
								</div>
							</div>
							<div className="RatingsCard-rating">
								<ArticleRating value={aggregatedRating} />
								<div className="RatingsCard-rating_count">
									{app.translator.trans('glowingblue-orell-rezensionen.lib.reviews', { reviews: totalReviewsCount })}
								</div>
							</div>
						</div>
						<RatingSummary aggregatedRatings={aggregatedRatings} totalReviewCount={totalReviewCount} work={work} />
					</div>
				)}
				{canMakeSimpleRating && (
					<div className="RatingsCard-row">
						<div className="RatingsCard-rating_wrapper">
							<ArticleRating
								onChange={this.saveRating.bind(this)}
								onDelete={canDeleteRating && this.saveRating.bind(this, 0)}
								value={this.rating()}
							/>
						</div>
					</div>
				)}
			</div>
		);
	}

	// `void` covers the fall-through that `noImplicitReturns` would otherwise flag.
	saveRating(rating: number): Promise<void> | void {
		const { article } = this.attrs;
		const ownReview = article.ownReview();
		const review = ownReview || app.store.createRecord('gb_reviews');

		if (rating === 0) {
			return review.delete().then(() => {
				this.refreshAndAlert(rating, app.translator.trans('glowingblue-orell-rezensionen.forum.article_page.ratings_card.rating_deleted'));
			});
		}

		review
			.save({
				rating,
				relationships: {
					article,
				},
			})
			.then(() => {
				this.refreshAndAlert(rating, app.translator.trans('glowingblue-orell-rezensionen.forum.article_page.ratings_card.rating_confirmed'));
			})
			.catch(() =>
				app.alerts.show(Alert, { type: 'error' }, app.translator.trans('glowingblue-orell-rezensionen.forum.article_page.ratings_card.rating_failed'))
			);
	}

	refreshAndAlert(rating: number, message: Mithril.Children) {
		const { article } = this.attrs;

		this.rating(rating);
		m.redraw();

		const alertId = app.alerts.show(Alert, { type: 'success' }, message);
		setTimeout(() => app.alerts.dismiss(alertId), 5000);

		// Pulls in the recomputed aggregates.
		app.store.find('of_articles', article.id()!).then(m.redraw);
	}
}
